'use client'

import { useEffect, useState } from 'react'
import { ScrollingGallery, StackedScrollingGallery, type GalleryImage } from '@/components/scrolling-gallery'
import galleryImages from '@/data/scrolling-gallery-images.json'

// Heights for layout calculations
// Mr Yarn: 140px image + ~50px bounce room = 190px
// Welcome card: ~380px (varies slightly)
// Single row: imageHeight + staggerOffset = varies by size
// Stacked: (imageHeight * 2) + verticalGap + staggerOffset = varies by size

interface HomeGalleryProps {
  className?: string
}

export function HomeGallery({ className = '' }: HomeGalleryProps) {
  const images = galleryImages as GalleryImage[]
  const [galleryMode, setGalleryMode] = useState<'single' | 'stacked' | 'hidden'>('hidden')
  const [imageHeight, setImageHeight] = useState(160)

  useEffect(() => {
    const calculateLayout = () => {
      const vh = window.innerHeight
      const vw = window.innerWidth

      // Compact mode for short screens, but not if width >= 640 and height >= 600
      const isCompact = vh < 600 || (vh < 700 && vw < 640)
      const isTall = vh >= 850
      const isNarrow = vw < 640

      const staggerOffset = 40
      const verticalGap = 16

      // Image size constraints
      const minHeight = isCompact ? 100 : (isNarrow ? 120 : 140)
      const maxSingleHeight = 220  // Max image height for single row

      // Double stack only from 900px height
      const useStacked = vh >= 900

      // Use percentage of viewport height for gallery
      // Gallery takes bottom portion of screen
      const galleryPercent = useStacked ? 0.43 : 0.43
      const availableForGallery = vh * galleryPercent

      if (useStacked) {
        // Stacked mode - fill available space
        const stackedImageHeight = Math.floor((availableForGallery - verticalGap - staggerOffset) / 2)
        setImageHeight(Math.max(stackedImageHeight, minHeight))
        setGalleryMode('stacked')
      } else {
        // Single row - expand images to fill available space
        const singleImageHeight = Math.min(availableForGallery - staggerOffset, maxSingleHeight)
        setImageHeight(Math.max(singleImageHeight, minHeight))
        setGalleryMode('single')
      }
    }

    calculateLayout()
    window.addEventListener('resize', calculateLayout)
    return () => window.removeEventListener('resize', calculateLayout)
  }, [])

  // Show placeholder while calculating layout (prevents layout shift)
  if (galleryMode === 'hidden') {
    // Reserve approximate space for single row
    return <div className={className} style={{ height: imageHeight + 40 }} />
  }

  const staggerOffset = 40
  const gap = 14
  const verticalGap = 16

  return (
    <div className={`w-full ${className}`}>
      {galleryMode === 'stacked' ? (
        <StackedScrollingGallery
          images={images}
          speed={50}
          imageHeight={imageHeight}
          horizontalGap={gap}
          verticalGap={verticalGap}
          staggerOffset={staggerOffset}
        />
      ) : (
        <ScrollingGallery
          images={images}
          speed={40}
          imageHeight={imageHeight}
          gap={gap}
          staggerOffset={staggerOffset}
        />
      )}
    </div>
  )
}
